import { IconButton, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Refresh } from '@material-ui/icons';
import React, { useCallback, useEffect, useRef } from 'react';

const CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const CAPTCHA_LENGTH = 5;
// Matches the height of a size="small" outlined TextField so the row lines up.
const WIDTH = 128;
const HEIGHT = 40;

export function generateCaptchaCode() {
    let code = "";
    for (let i = 0; i < CAPTCHA_LENGTH; i++) {
        code += CHARS.charAt(Math.floor(Math.random() * CHARS.length));
    }
    return code;
}

const useStyles = makeStyles((theme) => ({
    wrapper: {
        display: 'flex',
        alignItems: 'center',
    },
    canvas: {
        width: WIDTH,
        height: HEIGHT,
        borderRadius: 6,
        border: '1px solid rgba(255, 255, 255, 0.23)',
        display: 'block',
        userSelect: 'none',
    },
    refresh: {
        marginLeft: theme.spacing(0.5),
        padding: theme.spacing(0.75),
    }
}));

/**
 * Draws a throw away, client side captcha. It only slows down casual bots -
 * the real check still has to happen on the server.
 */
export default function Captcha({ code, onRefresh }) {
    const classes = useStyles();
    const canvasRef = useRef(null);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !code) {
            return;
        }

        const ratio = window.devicePixelRatio || 1;
        canvas.width = WIDTH * ratio;
        canvas.height = HEIGHT * ratio;

        const ctx = canvas.getContext("2d");
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        ctx.clearRect(0, 0, WIDTH, HEIGHT);

        const background = ctx.createLinearGradient(0, 0, WIDTH, HEIGHT);
        background.addColorStop(0, "#f4f7fb");
        background.addColorStop(1, "#dde5f0");
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, WIDTH, HEIGHT);

        // speckles
        for (let i = 0; i < 45; i++) {
            ctx.fillStyle = `rgba(1, 41, 92, ${0.1 + Math.random() * 0.3})`;
            ctx.beginPath();
            ctx.arc(Math.random() * WIDTH, Math.random() * HEIGHT, Math.random() * 1.3, 0, Math.PI * 2);
            ctx.fill();
        }

        // characters
        const step = WIDTH / (code.length + 1);
        ctx.textBaseline = "middle";
        for (let i = 0; i < code.length; i++) {
            const angle = (Math.random() - 0.5) * 0.6;
            const fontSize = 19 + Math.random() * 5;
            ctx.save();
            ctx.translate(step * (i + 1), HEIGHT / 2 + (Math.random() - 0.5) * 6);
            ctx.rotate(angle);
            ctx.font = `700 ${fontSize}px "Montserrat", sans-serif`;
            ctx.fillStyle = i % 2 === 0 ? "#01295C" : "#ee3638";
            ctx.textAlign = "center";
            ctx.fillText(code.charAt(i), 0, 0);
            ctx.restore();
        }

        // strike through lines
        for (let i = 0; i < 3; i++) {
            ctx.strokeStyle = `rgba(1, 41, 92, ${0.25 + Math.random() * 0.25})`;
            ctx.lineWidth = 1 + Math.random();
            ctx.beginPath();
            ctx.moveTo(0, Math.random() * HEIGHT);
            ctx.bezierCurveTo(
                WIDTH / 3, Math.random() * HEIGHT,
                (WIDTH / 3) * 2, Math.random() * HEIGHT,
                WIDTH, Math.random() * HEIGHT
            );
            ctx.stroke();
        }
    }, [code]);

    useEffect(() => {
        draw();
    }, [draw]);

    return (
        <div className={classes.wrapper}>
            <canvas
                ref={canvasRef}
                className={classes.canvas}
                style={{ width: WIDTH, height: HEIGHT }}
                aria-label="Captcha image"
                role="img"
            />
            <Tooltip title="Get a new code">
                <IconButton size="small" className={classes.refresh} onClick={onRefresh} aria-label="Refresh captcha">
                    <Refresh fontSize="small" />
                </IconButton>
            </Tooltip>
        </div>
    );
}
